/**
 * 音声再生。
 *
 * 優先順位（音ごとに別々に決まる。1つだけ置いても動く）:
 *   1. public/assets/audio/ に実ファイルがあればそれを再生する
 *   2. 無い場合は WebAudio による合成音（オリジナル）で鳴らす
 *   3. WebAudio も使えない環境では完全に無音で動作する
 *
 * 合体音は生成レベルに応じて音階が1段ずつ上がるため、
 * 連鎖すると旋律のように聞こえて手応えが出る。
 * **ファイルにしても同じ**：素材の再生速度を変えて、陽音階の高さに合わせる（config/audio.ts）。
 *
 * 効果音ファイルは WebAudio のバッファで鳴らす（<audio> を複製する方式はスマホで遅れて鳴るため）。
 * ぶつかった音（impact）は頻度が高く、ファイルにすると重なって濁るので合成音のまま。
 */

import { AudioEngine } from '../audio/AudioEngine';
import { ProceduralBgm } from '../audio/ProceduralBgm';
import {
  playBonusSfx,
  playComboSfx,
  playDropSfx,
  playGameOverSfx,
  playImpactSfx,
  playMergeSfx,
  playTopSfx,
  playUnlockSfx,
} from '../audio/sfx';
import { playbackRateForLevel, SAMPLE_TUNING } from '../config/audio';
import type { StageAudioPaths } from '../config/types';

export type SoundKey =
  'drop' | 'merge' | 'combo' | 'gameOver' | 'bonus' | 'unlock' | 'impact' | 'top';

/** 効果音の鳴らし分けに使う情報。 */
export interface SePlayOptions {
  /** 対象の料理レベル（合体音の音程に使う）。 */
  level?: number;
  /** 現在の連鎖数（コンボ音の重ねに使う）。 */
  combo?: number;
  /** ぶつかった強さ（0〜1）。 */
  strength?: number;
}

export interface AudioSystemOptions {
  paths?: StageAudioPaths;
  bgmVolume: number;
  seVolume: number;
  /** BGM ファイルの存在確認（差し替え可能 / 省略時は fetch でプローブ）。 */
  probe?: (url: string) => Promise<boolean>;
  /** 効果音ファイルの取得（差し替え可能 / 省略時は fetch。無ければ null）。 */
  loadSample?: (url: string) => Promise<ArrayBuffer | null>;
  /** 実ファイルが無いときに合成音を鳴らすか。 */
  useSynthFallback?: boolean;
}

async function defaultProbe(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: 'HEAD', cache: 'force-cache' });
    if (!response.ok) return false;
    const type = response.headers.get('content-type') ?? '';
    return !type.includes('text/html');
  } catch {
    return false;
  }
}

/** 効果音ファイルを取りに行く。無い・HTML（SPA のフォールバック）なら null。 */
async function defaultLoadSample(url: string): Promise<ArrayBuffer | null> {
  try {
    const response = await fetch(url, { cache: 'force-cache' });
    if (!response.ok) return null;
    const type = response.headers.get('content-type') ?? '';
    if (type.includes('text/html')) return null;
    return await response.arrayBuffer();
  } catch {
    return null;
  }
}

type FileKey = SoundKey & keyof typeof SAMPLE_TUNING;

const FILE_KEYS: FileKey[] = ['drop', 'merge', 'combo', 'top', 'gameOver', 'bonus', 'unlock'];

export class AudioSystem {
  private readonly options: AudioSystemOptions;
  private readonly samples = new Map<FileKey, AudioBuffer>();
  private readonly voices = new Map<FileKey, AudioBufferSourceNode[]>();
  private readonly engine = new AudioEngine();
  private bgmElement: HTMLAudioElement | null = null;
  private synthBgm: ProceduralBgm | null = null;
  private bgmVolume: number;
  private seVolume: number;
  private bgmIntensity = 0;
  private ready = false;
  private bgmRequested = false;

  constructor(options: AudioSystemOptions) {
    this.options = options;
    this.bgmVolume = clamp01(options.bgmVolume);
    this.seVolume = clamp01(options.seVolume);
    this.engine.setBgmVolume(this.bgmVolume);
    this.engine.setSeVolume(this.seVolume);
  }

  /** 実ファイルの存在確認と読み込み。失敗しても例外は投げない。 */
  async init(): Promise<void> {
    const paths = this.options.paths;
    if (!paths || typeof Audio === 'undefined') {
      this.ready = true;
      return;
    }
    const probe = this.options.probe ?? defaultProbe;
    const loadSample = this.options.loadSample ?? defaultLoadSample;

    await Promise.all(
      FILE_KEYS.map(async (key) => {
        const url = paths[key];
        if (!url) return;
        const data = await loadSample(url);
        if (!data) return;
        const bus = this.engine.seBusOrNull();
        if (!bus) return;
        try {
          this.samples.set(key, await bus.ctx.decodeAudioData(data));
        } catch {
          /* 読めない形式なら合成音へフォールバック */
        }
      }),
    );

    if (paths.bgm && (await probe(paths.bgm))) {
      try {
        const element = new Audio(paths.bgm);
        element.loop = true;
        element.preload = 'auto';
        element.volume = this.bgmVolume;
        this.bgmElement = element;
      } catch {
        this.bgmElement = null;
      }
    }

    this.ready = true;
    if (this.bgmRequested) this.playBgm();
  }

  /** 自動再生制限の解除。ユーザー操作の直後に呼ぶ。 */
  unlock(): void {
    this.engine.resume();
  }

  setVolumes(volumes: { bgmVolume?: number; seVolume?: number }): void {
    if (typeof volumes.bgmVolume === 'number') {
      this.bgmVolume = clamp01(volumes.bgmVolume);
      this.engine.setBgmVolume(this.bgmVolume);
      if (this.bgmElement) this.bgmElement.volume = this.bgmVolume;
      if (this.bgmVolume === 0) this.suspendBgm();
      else if (this.bgmRequested) this.playBgm();
    }
    if (typeof volumes.seVolume === 'number') {
      this.seVolume = clamp01(volumes.seVolume);
      this.engine.setSeVolume(this.seVolume);
    }
  }

  playSe(key: SoundKey, options: SePlayOptions = {}): void {
    if (this.seVolume <= 0) return;
    if (this.playSample(key, options)) return;
    if (this.options.useSynthFallback === false) return;
    this.playSynth(key, options);
  }

  playBgm(): void {
    this.bgmRequested = true;
    if (!this.ready || this.bgmVolume <= 0) return;

    if (this.bgmElement) {
      try {
        this.bgmElement.volume = this.bgmVolume;
        void this.bgmElement.play().catch(() => undefined);
      } catch {
        /* 自動再生が拒否されても無視する */
      }
      return;
    }

    if (this.options.useSynthFallback === false) return;
    this.engine.resume();
    const bgm = this.ensureSynthBgm();
    bgm?.start();
  }

  /** 広告が画面を覆うあいだだけBGMを止める（鳴らす予定は覚えておく）。 */
  pauseForAd(): void {
    this.suspendBgm();
  }

  /**
   * 広告が閉じたら、鳴らす予定だったBGMを戻す。
   * 2026-10-13：iPhone では広告が閉じた直後、音の再生権がまだアプリに戻っておらず、
   * 1回だけ鳴らし直すと空振りして止まったままになっていた。少し待って2回試し直し、
   * それでも鳴っていなければ、次に画面に触れたとき（ユーザー操作の直後なら必ず通る）に鳴らし直す。
   */
  resumeAfterAd(): void {
    if (!this.bgmRequested) return;
    const retry = () => {
      if (!this.bgmRequested) return;
      this.engine.resume();
      this.playBgm();
    };
    retry();
    if (typeof window === 'undefined') return;
    window.setTimeout(retry, 400);
    window.setTimeout(retry, 1500);
    if (this.resumeOnTouchArmed) return;
    this.resumeOnTouchArmed = true;
    const onTouch = () => {
      this.resumeOnTouchArmed = false;
      window.removeEventListener('pointerdown', onTouch, true);
      retry();
    };
    window.addEventListener('pointerdown', onTouch, true);
  }

  private resumeOnTouchArmed = false;

  stopBgm(): void {
    this.bgmRequested = false;
    this.suspendBgm();
  }

  /**
   * 名場面の録画用の音（効果音と合成BGM）。録っているあいだだけ分岐をつなぐ。
   * 実ファイルのBGM（<audio> で鳴らす）は WebAudio を通らないので入らない。
   */
  openRecordingStream(): MediaStream | null {
    return this.engine.openRecordingTap();
  }

  closeRecordingStream(): void {
    this.engine.closeRecordingTap();
  }

  /** その音を実ファイルで鳴らすか（試聴ページ・テスト用）。 */
  hasFile(key: SoundKey): boolean {
    return this.samples.has(key as FileKey);
  }

  /** 到達レベルに応じた盛り上がり（0〜1）。夜が深まるほど層が増える。 */
  setBgmIntensity(value: number): void {
    this.bgmIntensity = Math.min(1, Math.max(0, value));
    this.synthBgm?.setIntensity(this.bgmIntensity);
  }

  dispose(): void {
    this.stopBgm();
    this.synthBgm?.dispose();
    this.synthBgm = null;
    this.samples.clear();
    this.voices.clear();
    this.bgmElement = null;
    this.engine.close();
  }

  /* -------------------------------- 内部処理 -------------------------------- */

  private suspendBgm(): void {
    this.synthBgm?.stop();
    if (!this.bgmElement) return;
    try {
      this.bgmElement.pause();
    } catch {
      /* noop */
    }
  }

  private ensureSynthBgm(): ProceduralBgm | null {
    if (this.synthBgm) return this.synthBgm;
    const bus = this.engine.bgmBusOrNull();
    if (!bus) return null;
    this.synthBgm = new ProceduralBgm(bus);
    this.synthBgm.setIntensity(this.bgmIntensity);
    return this.synthBgm;
  }

  /**
   * 実ファイルで鳴らす。鳴らせたら true。
   * 連鎖（combo）の素材が無ければ、合体の素材を連鎖ぶん高くして代わりに使う。
   */
  private playSample(key: SoundKey, options: SePlayOptions): boolean {
    let fileKey = key as FileKey;
    let level = options.level ?? 2;
    if (fileKey === 'combo' && !this.samples.has('combo') && this.samples.has('merge')) {
      fileKey = 'merge';
      level += Math.min(4, Math.max(0, (options.combo ?? 2) - 1));
    }
    const buffer = this.samples.get(fileKey);
    if (!buffer) return false;
    const bus = this.engine.seBusOrNull();
    if (!bus) return false;
    this.engine.resume();
    try {
      const tuning = SAMPLE_TUNING[fileKey];
      const source = bus.ctx.createBufferSource();
      source.buffer = buffer;
      if (tuning.pitchByLevel) source.playbackRate.value = playbackRateForLevel(level);
      const gain = bus.ctx.createGain();
      gain.gain.value = tuning.gain;
      source.connect(gain).connect(bus.out);

      // 同時に鳴る数を抑える（いちばん古いものを止める）
      const active = this.voices.get(fileKey) ?? [];
      while (active.length >= tuning.maxVoices) {
        try {
          active.shift()?.stop();
        } catch {
          /* もう止まっている */
        }
      }
      active.push(source);
      this.voices.set(fileKey, active);
      source.onended = () => {
        const list = this.voices.get(fileKey);
        const index = list?.indexOf(source) ?? -1;
        if (list && index >= 0) list.splice(index, 1);
      };
      source.start();
      return true;
    } catch {
      return false;
    }
  }

  /** 実ファイルが無い場合の合成音。 */
  private playSynth(key: SoundKey, options: SePlayOptions): void {
    const bus = this.engine.seBusOrNull();
    if (!bus) return;
    this.engine.resume();
    const level = options.level ?? 2;
    try {
      switch (key) {
        case 'drop':
          playDropSfx(bus, level);
          break;
        case 'merge':
          playMergeSfx(bus, level);
          break;
        case 'combo':
          playComboSfx(bus, level, options.combo ?? 2);
          break;
        case 'bonus':
          playBonusSfx(bus);
          break;
        case 'unlock':
          playUnlockSfx(bus);
          break;
        case 'gameOver':
          playGameOverSfx(bus);
          break;
        case 'impact':
          playImpactSfx(bus, level, options.strength ?? 0.5);
          break;
        case 'top':
          playTopSfx(bus);
          break;
      }
    } catch {
      /* 音が鳴らなくてもゲームは続行する */
    }
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}
